/**
 * COMPARAR DOS AJUSTES (pantalla 01 del documento de Jesús del 7-09, «el comparador»).
 *
 * «Dos ajustes y su diferencia, con los ocho números. Es el mismo formato que ya decidiste para
 * las medidas en Evolución -- dos tomas y su diferencia --, así que no hay nada nuevo que
 * aprender.»
 *
 * Y por eso esto se parece tanto a `EvolucionMedidas`: mismas cuatro columnas (qué, antes,
 * ahora, la diferencia), mismo sitio para el selector y misma manera de decir que algo no
 * cambió. Lo que cambia es qué se compara y cómo se agrupa.
 *
 * LAS TRES REGLAS SUYAS, y de dónde sale cada una:
 *
 *   · LOS OCHO NÚMEROS, agrupados por tipo de día: entreno (P·H·G), perientreno (P·H) y
 *     descanso (P·H·G). No los tres del día de entreno, que es lo que enseña la portada.
 *   · LA DIFERENCIA, en rojo lo que cambió y en gris lo que se quedó igual, «con la misma
 *     regla que la tabla de arriba». Por eso el cero va en gris y no se esconde: que un macro
 *     NO se haya movido es información, y en un ajuste es de las que más dicen.
 *   · Y EL PESO ABAJO, SEPARADO: «es de otra naturaleza y por eso va con su línea».
 */
import React, { useState } from 'react';
import { num1 } from '../lib/numeros';
import { fechaCorta } from './HistorialDeMacros';

// Los tres bloques con sus campos, en el orden de siempre. El peri no lleva grasas.
const BLOQUES = [
    { key: 'entreno', label: 'Día de entreno', campos: ['proteina', 'hidratos', 'grasa'] },
    { key: 'peri', label: 'Perientreno', campos: ['proteina', 'hidratos'] },
    { key: 'descanso', label: 'Día de descanso', campos: ['proteina', 'hidratos', 'grasa'] },
];
// EN SINGULAR, COMO TODA LA APP (Jesús, 11-09): «Proteína · Hidratos · Grasa».
const NOMBRE = { proteina: 'Proteína', hidratos: 'Hidratos', grasa: 'Grasa' };

// «−20», «+35», «0». El signo menos es el de verdad (−, U+2212) y no un guion: en la columna
// de números un guion se lee como una raya de «no hay dato».
const conSigno = (d) => (d > 0 ? `+${num1(d)}` : d < 0 ? `−${num1(Math.abs(d))}` : '0');

const Celda = ({ children, className = '' }) => (
    <td className={`px-2 py-1.5 text-right font-data tabular-nums whitespace-nowrap ${className}`}>
        {children}
    </td>
);

const Fila = ({ label, antes, ahora }) => {
    const hay = antes != null && ahora != null;
    const d = hay ? ahora - antes : null;
    return (
        <tr className="border-b border-border last:border-0">
            <td className="px-2 py-1.5 text-muted-foreground">{label}</td>
            <Celda className="text-muted-foreground">{antes != null ? num1(antes) : '-'}</Celda>
            <Celda className="font-bold text-foreground">{ahora != null ? num1(ahora) : '-'}</Celda>
            <Celda className={d ? 'font-bold text-red-600 dark:text-red-400' : 'text-muted-foreground'}>
                {d == null ? '-' : conSigno(d)}
            </Celda>
        </tr>
    );
};

const ComparadorDeAjustes = ({ entradas }) => {
    const filas = entradas || [];
    // Por defecto, los dos últimos: es la comparación que contesta «¿qué me acabáis de mover?».
    // Las entradas llegan de la más nueva a la más vieja.
    const [iAhora, setIAhora] = useState(0);
    const [iAntes, setIAntes] = useState(1);

    // Con un solo ajuste no hay nada que comparar y el bloque no se pinta: un comparador con
    // una sola columna llena y la otra vacía no dice nada.
    if (filas.length < 2) return null;

    const ahora = filas[Math.min(iAhora, filas.length - 1)];
    const antes = filas[Math.min(iAntes, filas.length - 1)];

    const selector = (valor, onChange, testid) => (
        <select value={valor} onChange={e => onChange(Number(e.target.value))} data-testid={testid}
            className="flex-1 min-w-0 bg-card border border-border rounded-xl px-3 py-2 text-sm font-semibold text-foreground focus:outline-none focus:ring-1 focus:ring-brand">
            {filas.map((f, i) => (
                <option key={f.id || i} value={i}>{fechaCorta(f.fecha)}</option>
            ))}
        </select>
    );

    return (
        <section className="space-y-2" data-testid="comparador-de-ajustes">
            <p className="caption">Comparar dos ajustes</p>
            <div className="surface p-4 space-y-3">
                <div className="flex items-center gap-2">
                    {selector(iAntes, setIAntes, 'comparar-antes')}
                    <span className="text-xs text-muted-foreground flex-none">contra</span>
                    {selector(iAhora, setIAhora, 'comparar-ahora')}
                </div>

                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                            <th className="text-left font-semibold px-2 py-1.5"> </th>
                            <th className="text-right font-semibold px-2 py-1.5">{fechaCorta(antes.fecha)}</th>
                            <th className="text-right font-semibold px-2 py-1.5">{fechaCorta(ahora.fecha)}</th>
                            <th className="text-right font-semibold px-2 py-1.5">Dif</th>
                        </tr>
                    </thead>
                    <tbody>
                        {BLOQUES.map(b => (
                            <React.Fragment key={b.key}>
                                <tr>
                                    <td colSpan={4} className="px-2 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-brand">
                                        {b.label}
                                    </td>
                                </tr>
                                {b.campos.map(c => (
                                    <Fila key={`${b.key}-${c}`} label={NOMBRE[c]}
                                        antes={antes[b.key]?.[c]} ahora={ahora[b.key]?.[c]} />
                                ))}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>

                {/* EL PESO, ABAJO Y CON SU LÍNEA: «es de otra naturaleza». No va en la tabla de
                    arriba porque no es un macro que le pusimos nosotros, es lo que hizo su
                    cuerpo, y mezclarlo con los ocho lo convertiría en un noveno número más. */}
                <table className="w-full text-sm border-t-2 border-border pt-1">
                    <tbody>
                        <tr>
                            <td className="px-2 py-2 font-semibold text-foreground">Peso</td>
                            <Celda className="text-muted-foreground">{antes.peso != null ? `${num1(antes.peso)}` : '-'}</Celda>
                            <Celda className="font-bold text-foreground">{ahora.peso != null ? `${num1(ahora.peso)}` : '-'}</Celda>
                            <Celda className={
                                antes.peso != null && ahora.peso != null && ahora.peso !== antes.peso
                                    ? 'font-bold text-red-600 dark:text-red-400' : 'text-muted-foreground'}>
                                {antes.peso != null && ahora.peso != null ? conSigno(ahora.peso - antes.peso) : '-'}
                            </Celda>
                        </tr>
                    </tbody>
                </table>
            </div>
        </section>
    );
};

export default ComparadorDeAjustes;
